// src/components/Widgets/ParticipantsWidget.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo, ROUTES } from "../../lib/nav";
import { useParticipants } from "../../contexts/ParticipantContext";
import WidgetHeader from "../Common/WidgetHeader";

export default function ParticipantsWidget({ backTo = "/dashboard" }) {
  const navigate = useNavigate();
  const { pieces = [], updatePiece } = useParticipants();

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (pieces || [])
      .slice()
      .sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0))
      .filter((p) => (status === "all" ? true : (p.status || "Created") === status))
      .filter((p) => {
        if (!needle) return true;
        const hay = [
          p.title,
          p.email,
          p.name,
          p.workshopTitle,
          p.status,
          p.notes,
          p.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      });
  }, [pieces, q, status]);

  const statuses = ["all", "Created", "In Progress", "Bisque", "Glazed", "Ready", "Picked Up"];

  const badgeCls = (s) => {
    const map = {
      Created: "bg-zinc-700/30 text-zinc-200 border border-white/10",
      "In Progress": "bg-blue-600/30 text-blue-200 border border-white/10",
      Bisque: "bg-amber-600/30 text-amber-200 border border-white/10",
      Glazed: "bg-indigo-600/30 text-indigo-200 border border-white/10",
      Ready: "bg-emerald-600/30 text-emerald-200 border border-white/10",
      "Picked Up": "bg-neutral-500/30 text-neutral-200 border border-white/10",
    };
    return `px-2 py-0.5 rounded text-[11px] ${map[s] || "bg-white/10 text-white/80 border border-white/10"}`;
  };

  const quickAdvance = (p) => {
    const flow = ["Created", "In Progress", "Bisque", "Glazed", "Ready", "Picked Up"];
    const i = flow.indexOf(p.status || "Created");
    const next = flow[Math.min(flow.length - 1, Math.max(0, i + 1))];
    if (next !== p.status) {
      updatePiece?.({ ...p, status: next, updatedAt: Date.now() });
    }
  };

  return (
    <div className="rounded-lg p-4 h-full flex flex-col bg-transparent text-inherit">
      <WidgetHeader
        title="Participants"
        to={backTo}
        right={
          <button
            onClick={() => navigateTo(navigate, ROUTES.MY_PIECES, { source: 'ParticipantsWidget:open' })}
            className="px-2 py-1 text-xs rounded text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: 'var(--color-primary)' }}
            title="Open full participant tracker"
          >
            Open
          </button>
        }
      />

      {/* Filters */}
      <div className="grid grid-cols-3 gap-2 items-end mb-3">
        <label className="col-span-2">
          <div className="text-[11px] opacity-70 mb-1">Search</div>
          <input
            className="w-full px-2 py-1.5 rounded bg-black/30 border border-white/10 text-white placeholder-white/60"
            placeholder="Name, email, workshop…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search participants"
          />
        </label>
        <label>
          <div className="text-[11px] opacity-70 mb-1">Status</div>
          <select
            className="w-full px-2 py-1.5 rounded bg-black/30 border border-white/10 text-white"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
          >
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All" : s}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {filtered.length === 0 ? (
          <div className="text-sm opacity-70 p-3 border border-white/10 rounded bg-white/5">
            No matching pieces.
          </div>
        ) : (
          filtered.slice(0, 6).map((p) => (
            <div
              key={p.id}
              className="p-2 rounded border border-white/10 bg-white/5 hover:bg-white/10 transition"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div
                      className="font-medium truncate"
                      title={p.title || p.defaultTitle || "Untitled piece"}
                    >
                      {p.title || p.defaultTitle || "Untitled piece"}
                    </div>
                    <span className={badgeCls(p.status || "Created")}>
                      {p.status || "Created"}
                    </span>
                  </div>
                  <div className="text-xs opacity-70 truncate">
                    {p.name || "Guest"} • {p.email || "no email"} •{" "}
                    {p.workshopTitle || "Workshop"}
                  </div>
                </div>
                <button
                  onClick={() => quickAdvance(p)}
                  className="px-2 py-1 rounded text-xs text-white transition-colors hover:opacity-90 shrink-0"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                  title="Advance status"
                >
                  Advance
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {filtered.length > 6 && (
        <button
          onClick={() => navigateTo(navigate, ROUTES.MY_PIECES, { source: 'ParticipantsWidget:viewAll' })}
          className="w-full px-3 py-2 rounded text-sm border border-white/10 bg-white/5 hover:bg-white/10 mt-2"
        >
          View all…
        </button>
      )}

      <p className="text-[11px] opacity-60 mt-2">
        New workshop bookings that opt-in will auto-create pieces here.
      </p>
    </div>
  );
}
